import type { Environment } from '#app/shared/contexts/AppContext'
import * as branch from 'branch-sdk'
import type { AuthContextResult } from '../contexts/AuthContext'
import type { EventData } from './eventPublishing'

/**
 * Journeys (Branch's smart banners and interstitials) are configured in the
 * Branch dashboard and render over the host page on init. The portal shares the
 * mobile app's Branch app, so leaving them enabled would let a campaign created
 * for mobile inject UI into the carrier portal with no change on this side.
 */
const BranchInitOptions: branch.InitOptions = {
  no_journeys: true
}

/**
 * AuthContext falls back to `FALLBACK:<email>` when the backend has no user id.
 * A Branch identity is a long-lived cross-device key, so it must never be
 * handed an email address.
 */
const FallbackUserKeyPrefix = 'FALLBACK:'

let initialized = false

/**
 * Initialize the Branch Web SDK.
 *
 * Branch powers web-to-app deep linking and install attribution. Deep links
 * must be minted on the Branch link domain (`qbw7h.app.link`) rather than this
 * app's own hostname: `app.truenorthfleet.com` is not a claimed
 * associated-domain for the mobile app, so a link on it opens the browser
 * rather than the app.
 *
 * When no key is configured (local development) the SDK is never initialized
 * and every other function here becomes a no-op.
 */
export const initializeBranch = (environment: Environment) => {
  const branchKey = environment.BRANCH_KEY

  if (!branchKey) {
    console.error('Branch key is not set')
    return
  }

  // init reports failure asynchronously through this callback -- a revoked key
  // or a blocked api2.branch.io (Branch hosts appear on ad-block lists) fails
  // here, and the SDK must not be driven afterwards.
  branch.init(branchKey, BranchInitOptions, (error) => {
    if (error) {
      console.error('Branch failed to initialize', error)
      return
    }
    initialized = true
  })
}

/**
 * Set the user identity with Branch, so web sessions stitch to the same
 * identity the mobile app sets via `react-native-branch`.
 * @param authContext
 */
export const setBranchUser = (authContext: AuthContextResult | null) => {
  if (!initialized) {
    return
  }

  if (!authContext?.authenticated) {
    branch.logout()
    return
  }

  const { userKey } = authContext

  if (!userKey || userKey.startsWith(FallbackUserKeyPrefix)) {
    // Mobile always identifies by user id, so a fallback key could not stitch
    // to an app session even if it were safe to send.
    console.debug('Skipping Branch identity: no stable user id')
    return
  }

  branch.setIdentity(userKey)
}

/**
 * Branch custom data holds scalars only, so a nested value (every event carries
 * a `deviceInfo` object) would otherwise arrive as "[object Object]".
 */
const toBranchCustomData = (properties: EventData) =>
  Object.entries(properties).reduce<Record<string, string>>((result, [key, value]) => {
    if (value === undefined) {
      return result
    }
    result[key] = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)
    return result
  }, {})

/*
 * @param event The event name, uppercase with underscores to match the event
 *   types defined by the TrueNorth event ingestion pipeline.
 * @param properties A dictionary with key-value pairs, The "keys" must be in camelCase.
 */
export const publishBranchEvent = (event: string, properties: EventData) => {
  if (!initialized) {
    return
  }

  try {
    // Branch validates the event server-side (event names have a length limit),
    // and the callback is the only channel that surfaces a rejection.
    branch.logEvent(event, toBranchCustomData(properties), [], undefined, (error) => {
      if (error) {
        console.error('Branch rejected event', event, error)
      }
    })
  } catch (error) {
    console.error('Error publishing Branch event', error)
  }
}

// exported for testability
export const resetBranch = () => {
  initialized = false
}
